/**
 * GA4 client_id + Google Ads gclid attribution helpers.
 * Used to forward real-browser attribution from frontend → server-side GA4/Ads events.
 */

const GCLID_STORAGE_KEY = 'ma_gclid';
const GCLID_EXPIRY_KEY = 'ma_gclid_expires';
const GCLID_TTL_DAYS = 90;

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * GA4 client_id derived from the `_ga` cookie.
 * Cookie format: GA1.{depth}.{client_id}.{timestamp}
 * GA4 MP API expects: `{client_id}.{timestamp}`
 */
export function getGaClientId(): string | null {
  const raw = readCookie('_ga');
  if (!raw) return null;
  const parts = raw.split('.');
  if (parts.length < 4) return null;
  return `${parts[2]}.${parts[3]}`;
}

/** Capture gclid from current URL into localStorage. Call once on app init. */
export function captureGclidFromUrl(): void {
  try {
    const gclid = new URLSearchParams(window.location.search).get('gclid');
    if (gclid) {
      localStorage.setItem(GCLID_STORAGE_KEY, gclid);
      localStorage.setItem(GCLID_EXPIRY_KEY, String(Date.now() + GCLID_TTL_DAYS * 86400 * 1000));
    }
  } catch (_) { /* noop */ }
}

/** Current gclid — URL param wins, else stored value (if not expired). */
export function getGclid(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('gclid');
    if (fromUrl) return fromUrl;
    const stored = localStorage.getItem(GCLID_STORAGE_KEY);
    const expires = Number(localStorage.getItem(GCLID_EXPIRY_KEY) || 0);
    if (stored && expires > Date.now()) return stored;
  } catch (_) { /* noop */ }
  return null;
}

/** Returns both values for inclusion in backend payloads. */
export function getGaAttribution(): { ga_client_id: string | null; gclid: string | null } {
  return { ga_client_id: getGaClientId(), gclid: getGclid() };
}

/**
 * GA4 native session_id from _ga_XXXXXXXXXX cookie.
 * Cookie format: GS1.1.<session_id>.<session_count>...
 * Scans all cookies starting with _ga_ to work regardless of measurement ID.
 */
export function getGaSessionId(): string | null {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name && name.startsWith('_ga_') && value) {
        const parts = decodeURIComponent(value).split('.');
        // GS1.1.<session_id>.<session_count>...
        const sessionId = parts[2];
        if (sessionId) return sessionId;
      }
    }
  } catch (_) { /* noop */ }
  return null;
}

/** UTM params — reads from URL first, falls back to sessionStorage cache. */
export function getUtmParams(): { source: string; medium: string; campaign: string; term: string; content: string; id: string } {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
    keys.forEach(k => { if (params.get(k)) sessionStorage.setItem(k, params.get(k)!); });

    return {
      source:   params.get('utm_source')   || sessionStorage.getItem('utm_source')   || '',
      medium:   params.get('utm_medium')   || sessionStorage.getItem('utm_medium')   || '',
      campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
      term:     params.get('utm_term')     || sessionStorage.getItem('utm_term')     || '',
      content:  params.get('utm_content')  || sessionStorage.getItem('utm_content')  || '',
      id:       params.get('utm_id')       || sessionStorage.getItem('utm_id')       || '',
    };
  } catch (_) {
    return { source: '', medium: '', campaign: '', term: '', content: '', id: '' };
  }
}

/** Read IP geo cache set by DataLayerService.init() → ipapi.co */
function getIpData(): { ip_address: string; city: string; region: string; country: string } {
  try {
    const raw = sessionStorage.getItem('ip_data');
    if (raw) {
      const d = JSON.parse(raw);
      return {
        ip_address: d.ip_address || '',
        city:       d.city       || '',
        region:     d.region     || '',
        country:    d.country    || '',
      };
    }
  } catch (_) {}
  return { ip_address: '', city: '', region: '', country: '' };
}

/** Full attribution payload for backend API calls (add_to_cart, checkout etc). */
export function getFullAttribution(): Record<string, string> {
  const { ga_client_id, gclid } = getGaAttribution();
  const { source, medium, campaign, term, content, id } = getUtmParams();
  const ga_session_id = getGaSessionId();
  const page_location = typeof window !== 'undefined' ? window.location.href : '';
  const user_agent    = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const geo           = getIpData();

  const result: Record<string, string> = { page_location };
  if (ga_client_id)      result['ga_client_id']  = ga_client_id;
  if (gclid)             result['gclid']          = gclid;
  if (source)            result['source']         = source;
  if (medium)            result['medium']         = medium;
  if (campaign)          result['campaign']       = campaign;
  if (term)              result['term']           = term;
  if (content)           result['content']        = content;
  if (id)                result['utm_id']         = id;
  if (ga_session_id)     result['ga_session_id']  = ga_session_id;
  if (user_agent)        result['user_agent']     = user_agent;
  if (geo.ip_address)    result['ip_address']     = geo.ip_address;
  if (geo.city)          result['city']           = geo.city;
  if (geo.country)       result['country']        = geo.country;
  return result;
}
